import { Pencil } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useMe } from '@/api/domains/merchant-settings';
import { useOrderDetails } from '@/api/domains/order-shipments';
import type { OrderBillingAddress, OrderDetails, OrderProduct } from '@/api/types/order-shipments';
import type { Order, ShopifyAddress } from '@/api/types/orders';
import type { Shipment } from '@/api/types/shipments';
import { BackArrowIcon } from '@/components/icons/shipment-icons';
import { usePageTitle } from '@/components/layout/page-title';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { resolveLocale } from '@/i18n';
import { formatCurrency } from '@/lib/format/currency';

import { FinancialStatusBadge } from '../shipments/StatusBadge';
import { ShipmentBox } from './ShipmentBox';

const summaryTextClasses = 'text-base font-semibold leading-6 text-foreground';

function formatPrice(amount: string | null, currency: string | null, locale: string): string {
  if (amount === null || currency === null) return '-';
  const value = Number(amount);
  return Number.isFinite(value) ? formatCurrency(value, currency, locale) : '-';
}

function ProductsTable({
  products,
  order,
  locale,
}: {
  products: OrderProduct[];
  order: Order;
  locale: string;
}) {
  const { t } = useTranslation('orders');
  const headerCell =
    'h-[58px] border-y border-[#e9e9e9] p-3 text-left text-xs font-medium uppercase leading-none text-black';
  const totalQuantity = products.reduce((sum, product) => sum + product.quantity, 0);

  return (
    <table className="w-full">
      <thead>
        <tr>
          <th className={`${headerCell} w-[40%]`}>{t('detail.products.product')}</th>
          <th className={`${headerCell} w-[17%]`}>{t('detail.products.unitPrice')}</th>
          <th className={`${headerCell} w-[21%]`}>{t('detail.products.quantity')}</th>
          <th className={headerCell}>{t('detail.products.priceTotal')}</th>
        </tr>
      </thead>
      <tbody>
        {products.map((product, index) => (
          <tr key={product.id ?? index}>
            <td className="border-b border-[#e9e9e9] py-[15px] pr-3">
              <div className="flex items-center gap-[19px]">
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt=""
                    className="size-[61px] shrink-0 rounded-[12px] object-cover"
                  />
                ) : (
                  <div className="size-[61px] shrink-0 rounded-[12px] bg-[#e9e9e9]" />
                )}
                <span className="text-sm leading-5 text-foreground">{product.title}</span>
              </div>
            </td>
            <td className="border-b border-[#e9e9e9] p-3 text-sm leading-5 text-foreground">
              {formatPrice(product.unit_price, product.currency ?? order.shopify_currency, locale)}
            </td>
            <td className="border-b border-[#e9e9e9] p-3 text-sm leading-5 text-foreground">
              {product.quantity}
            </td>
            <td className="border-b border-[#e9e9e9] p-3 text-sm leading-5 text-foreground">
              {formatPrice(product.total_price, product.currency ?? order.shopify_currency, locale)}
            </td>
          </tr>
        ))}
        <tr>
          <td className={`h-[54px] border-b border-[#e9e9e9] py-3 pr-3 ${summaryTextClasses}`}>
            {t('detail.products.summary')}
          </td>
          <td className="border-b border-[#e9e9e9]" />
          <td className={`border-b border-[#e9e9e9] p-3 ${summaryTextClasses}`}>
            {t('detail.products.count', { count: totalQuantity })}
          </td>
          <td className={`border-b border-[#e9e9e9] p-3 ${summaryTextClasses}`}>
            {formatPrice(order.shopify_subtotal_price, order.shopify_currency, locale)}
          </td>
        </tr>
        {/* Delivery + grand total: the subtotal above is products-only, so
            without these the order total (and a COD amount derived from it)
            looks unrelated to the figures shown. Stored Shopify values are
            displayed as-is - no client-side arithmetic. */}
        <tr>
          <td
            colSpan={3}
            className="border-b border-[#e9e9e9] py-3 pr-3 text-sm leading-5 text-foreground"
          >
            {t('detail.products.delivery')}
          </td>
          <td className="border-b border-[#e9e9e9] p-3 text-sm leading-5 text-foreground">
            {formatPrice(order.shopify_shipping_price, order.shopify_currency, locale)}
          </td>
        </tr>
        <tr>
          <td colSpan={3} className={`border-b border-[#e9e9e9] py-3 pr-3 ${summaryTextClasses}`}>
            {t('detail.products.total')}
          </td>
          <td className={`border-b border-[#e9e9e9] p-3 ${summaryTextClasses}`}>
            {formatPrice(order.shopify_total_price, order.shopify_currency, locale)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function AddressCard({
  title,
  lines,
  email,
  phone,
  editHref,
  note,
}: {
  title: string;
  lines: (string | null | undefined)[];
  email?: string | null;
  phone?: string | null;
  /** Shopify admin order URL; renders an edit link (opens a new tab) when set. */
  editHref?: string | null;
  /** Muted hint under the title (e.g. "replaced with the pickup point's address"). */
  note?: string | null;
}) {
  const { t } = useTranslation('orders');
  const visibleLines = lines.filter((line): line is string => Boolean(line && line.trim()));

  return (
    <div className="flex min-h-[240px] flex-col gap-6 rounded-lg bg-[#f8f8f8] p-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-semibold uppercase leading-none text-black">{title}</p>
          {note ? <p className="text-xs leading-none text-[#737373]">{note}</p> : null}
        </div>
        {editHref ? (
          <a
            href={editHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('detail.editAddressAria')}
            className="inline-flex items-center gap-1 text-xs font-medium leading-none text-black hover:text-primary"
          >
            <Pencil className="size-3.5" />
            {t('detail.editAddress')}
          </a>
        ) : null}
      </div>
      <div className="h-px w-full bg-[#d9d9d9]" />
      {visibleLines.length === 0 && !email && !phone ? (
        <p className="text-sm leading-6 text-[#737373]">{t('detail.addressMissing')}</p>
      ) : (
        <p className="text-sm leading-6 text-[#737373]">
          {visibleLines.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
          {email ? (
            <span className="block">
              {t('detail.emailPrefix')} {email}
            </span>
          ) : null}
          {phone ? (
            <span className="block">
              {t('detail.phonePrefix')} {phone}
            </span>
          ) : null}
        </p>
      )}
    </div>
  );
}

function shippingAddressLines(address: ShopifyAddress | null): (string | null | undefined)[] {
  if (!address) return [];
  const name =
    address.name ?? [address.first_name, address.last_name].filter(Boolean).join(' ') ?? null;
  const cityLine = [address.zip, address.city].filter(Boolean).join(' ');

  return [name, address.address1, address.address2, cityLine];
}

function billingAddressLines(address: OrderBillingAddress | null): (string | null | undefined)[] {
  if (!address) return [];
  const cityLine = [address.zip, address.city].filter(Boolean).join(' ');

  return [address.name, address.address1, address.address2, cityLine];
}

function OrderDetailContent({ details }: { details: OrderDetails }) {
  const { t, i18n } = useTranslation('orders');
  const locale = resolveLocale(i18n.language);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const meQuery = useMe();
  const {
    order,
    shipments,
    products,
    billing_address: billingAddress,
    allowed_methods: allowedMethods,
    cash_on_delivery: orderCashOnDelivery,
    c2c: orderC2c,
    cod_currency: orderCodCurrency,
    insurance_currency: orderInsuranceCurrency,
    cod_available: orderCodAvailable = true,
    insurance_available: orderInsuranceAvailable = true,
    package_templates: packageTemplates,
    parcel_limits: parcelLimits,
    drop_off_limits: dropOffLimits,
  } = details;

  // Addresses are edited in Shopify admin (read-only here); link straight to the
  // order page on admin.shopify.com (store handle = shop domain minus the
  // .myshopify.com suffix).
  const shopDomain = meQuery.data?.merchant.shop_domain;
  const adminOrderUrl = shopDomain
    ? `https://admin.shopify.com/store/${shopDomain.replace('.myshopify.com', '')}/orders/${order.shopify_order_id}`
    : null;

  // Deep-link target from the shipments listing (?shipment=<id>): the matching
  // box scrolls into view and flashes.
  const highlightShipmentId = searchParams.get('shipment');

  // Removable split-shipment drafts, tracked by stable id so removing one box
  // never shuffles the others' local state. The implicit first draft shown when
  // the order has no shipments yet is rendered separately and is not removable.
  const draftIdRef = useRef(0);
  const [extraDraftIds, setExtraDraftIds] = useState<number[]>([]);
  const addDraft = () => {
    // Read/increment the ref outside the updater - the updater must stay pure
    // (React double-invokes it in Strict Mode).
    const id = draftIdRef.current++;
    setExtraDraftIds((ids) => [...ids, id]);
  };
  const removeDraft = (id: number) =>
    setExtraDraftIds((ids) => ids.filter((draftId) => draftId !== id));

  const shippingAddress = order.shopify_shipping_address;
  // A create invalidates the detail query, but its refetch lands a round-trip
  // later (and queries are 5 min stale by default, with no refetch on focus) -
  // until then a sibling draft would still believe the order carries nothing.
  // Shipments created from this page therefore count the moment they exist.
  const [locallyCreated, setLocallyCreated] = useState<Shipment[]>([]);
  const knownShipments = [
    ...shipments,
    ...locallyCreated.filter((created) => !shipments.some(({ id }) => id === created.id)),
  ];
  // Only the first shipment (order has none yet) may update the order's selected
  // point; once any shipment exists, points are per-shipment only.
  const canUpdateOrderPoint = knownShipments.length === 0;
  // A cancelled shipment collects nothing and splits nothing - the backend
  // ignores it the same way when deciding what a new shipment may carry.
  const activeShipments = knownShipments.filter((shipment) => shipment.status !== 'CANCELLED');
  // What the order's existing shipments already collect on delivery: a further
  // COD draft skips the order-total prefill and confirms before creating.
  const collectedCod = activeShipments
    .map((shipment) => shipment.cod)
    .filter((cod) => cod !== null);
  const rememberCreated = (shipment: Shipment) =>
    setLocallyCreated((created) => [...created, shipment]);
  const orderCodCollected =
    collectedCod.length > 0
      ? {
          // Amounts in another currency are never summed into this total (they
          // share the destination-resolved one in practice).
          amount: collectedCod
            .filter((cod) => cod.currency === collectedCod[0].currency)
            .reduce((sum, cod) => sum + Number(cod.amount), 0),
          currency: collectedCod[0].currency,
        }
      : null;

  return (
    <div className="mx-auto flex max-w-[940px] flex-col gap-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-[15px]">
          <button
            type="button"
            onClick={() => navigate('/shipments')}
            aria-label={t('detail.backToList')}
            className="grid size-9 shrink-0 place-items-center rounded-[10px] border border-black bg-transparent hover:bg-accent"
          >
            <BackArrowIcon className="size-5" />
          </button>
          <h2 className="text-4xl font-medium leading-10 tracking-[-0.9px] text-black">
            {t('detail.title', { number: order.shopify_order_number })}
          </h2>
          {order.shopify_financial_status ? (
            <FinancialStatusBadge status={order.shopify_financial_status} />
          ) : null}
        </div>
        {/* Visual parity with the design; the tour itself is not implemented yet. */}
        <Button variant="outline" className="border-black text-black hover:bg-accent" type="button">
          {t('detail.quickTour')}
        </Button>
      </div>

      {products ? (
        <ProductsTable products={products} order={order} locale={locale} />
      ) : (
        <p className="border-y border-[#e9e9e9] py-6 text-sm text-muted-foreground">
          {t('detail.productsUnavailable')}
        </p>
      )}

      {order.service_type ? (
        <div className="flex flex-col items-center gap-6">
          {knownShipments.map((shipment) => (
            <ShipmentBox
              key={shipment.id}
              order={order}
              shipment={shipment}
              canUpdateOrderPoint={canUpdateOrderPoint}
              allowedMethods={allowedMethods}
              orderCashOnDelivery={orderCashOnDelivery}
              orderC2c={orderC2c}
              orderCodCurrency={orderCodCurrency}
              orderInsuranceCurrency={orderInsuranceCurrency}
              orderCodAvailable={orderCodAvailable}
              orderInsuranceAvailable={orderInsuranceAvailable}
              packageTemplates={packageTemplates}
              parcelLimits={parcelLimits}
              dropOffLimits={dropOffLimits}
              highlight={String(shipment.id) === highlightShipmentId}
            />
          ))}
          {/* The implicit first draft (order has no shipments yet) owns the
              order's selected point and is not removable. */}
          {knownShipments.length === 0 ? (
            <ShipmentBox
              key="primary-draft"
              order={order}
              shipment={null}
              canUpdateOrderPoint={canUpdateOrderPoint}
              allowedMethods={allowedMethods}
              orderCashOnDelivery={orderCashOnDelivery}
              orderC2c={orderC2c}
              orderCodCurrency={orderCodCurrency}
              orderInsuranceCurrency={orderInsuranceCurrency}
              orderCodAvailable={orderCodAvailable}
              orderInsuranceAvailable={orderInsuranceAvailable}
              packageTemplates={packageTemplates}
              parcelLimits={parcelLimits}
              dropOffLimits={dropOffLimits}
              onCreated={rememberCreated}
            />
          ) : null}
          {/* Extra split drafts: per-shipment point only, each individually
              removable until it is created. */}
          {extraDraftIds.map((id) => (
            <ShipmentBox
              key={`draft-${id}`}
              order={order}
              shipment={null}
              canUpdateOrderPoint={false}
              allowedMethods={allowedMethods}
              orderCashOnDelivery={orderCashOnDelivery}
              orderC2c={orderC2c}
              orderCodCurrency={orderCodCurrency}
              orderInsuranceCurrency={orderInsuranceCurrency}
              orderCodAvailable={orderCodAvailable}
              orderInsuranceAvailable={orderInsuranceAvailable}
              packageTemplates={packageTemplates}
              parcelLimits={parcelLimits}
              dropOffLimits={dropOffLimits}
              orderHasShipments={activeShipments.length > 0}
              orderCodCollected={orderCodCollected}
              onCreated={(shipment) => {
                rememberCreated(shipment);
                removeDraft(id);
              }}
              onRemove={() => removeDraft(id)}
            />
          ))}
          <button
            type="button"
            onClick={addDraft}
            className="flex h-9 items-center justify-center rounded-[10px] border border-black bg-transparent px-4 text-sm font-medium leading-5 text-black hover:bg-accent"
          >
            {t('detail.splitShipment')}
          </button>
        </div>
      ) : (
        <p className="rounded-lg bg-[#f8f8f8] p-6 text-sm text-muted-foreground">
          {t('detail.noService')}
        </p>
      )}

      <div className="grid gap-[30px] md:grid-cols-2">
        <AddressCard
          title={t('detail.shippingAddress')}
          lines={shippingAddressLines(shippingAddress)}
          email={order.shopify_email}
          phone={order.shopify_phone ?? shippingAddress?.phone}
          editHref={adminOrderUrl}
          note={order.shipping_address_replaced ? t('detail.addressReplacedNote') : null}
        />
        <AddressCard
          title={t('detail.billingAddress')}
          lines={billingAddressLines(billingAddress)}
          email={billingAddress ? order.shopify_email : null}
          phone={billingAddress?.phone}
          editHref={adminOrderUrl}
        />
        {order.shipping_address_replaced ? (
          <AddressCard
            title={t('detail.originalShippingAddress')}
            lines={shippingAddressLines(order.original_shipping_address)}
            phone={order.original_shipping_address?.phone}
          />
        ) : null}
      </div>
    </div>
  );
}

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('orders');
  usePageTitle(t('title'));

  const detailsQuery = useOrderDetails(id);

  if (detailsQuery.isLoading) {
    return (
      <div className="mx-auto flex max-w-[940px] flex-col gap-10">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-[200px] w-full" />
      </div>
    );
  }

  if (detailsQuery.isError || !detailsQuery.data) {
    return (
      <div className="mx-auto flex max-w-[940px] flex-col items-center gap-4 py-10">
        <p className="text-sm text-muted-foreground">{t('detail.loadFailed')}</p>
        <button
          type="button"
          onClick={() => void detailsQuery.refetch()}
          className="flex h-9 items-center justify-center rounded-[10px] border border-black bg-transparent px-4 text-sm font-medium leading-5 text-black hover:bg-accent"
        >
          {t('redirect.retry')}
        </button>
      </div>
    );
  }

  return <OrderDetailContent key={detailsQuery.data.order.id} details={detailsQuery.data} />;
}
